import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Sidebar from '../../components/ui/Sidebar';
import UserAccountMenu from '../../components/ui/UserAccountMenu';
import EmailTemplateCard from './components/EmailTemplateCard';
import CategoryFilter from './components/CategoryFilter';
import EmailStatsBar from './components/EmailStatsBar';
import EmptyState from './components/EmptyState';
import EmailEditModal from './components/EmailEditModal';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const EmailCampaigns = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [emailTemplates, setEmailTemplates] = useState([
    {
      id: 1,
      title: "Introducing Videra Surgical: Precision Redefined",
      category: "technology",
      subject: "Revolutionary Tissue Marking Technology Now Available",
      previewText: "Discover how Videra Surgical is transforming surgical precision with our adaptable tissue marker",
      bodyContent: `Dear [Practice Name] Patients,\n\nWe are excited to announce that our practice now offers Videra Surgical, an innovative adaptable tissue marker that represents a significant advancement in surgical precision.\n\nVidera Surgical's unique curved design allows for optimal tissue marking in even the most challenging anatomical locations, ensuring accurate surgical planning and improved patient outcomes.\n\nKey Benefits:\n• Enhanced surgical precision with adaptable design\n• Improved visualization during procedures\n• Reduced procedure time\n• Better patient outcomes\n\nOur commitment to utilizing the latest medical technology ensures you receive the highest standard of care. We believe Videra Surgical will significantly enhance your surgical experience.\n\nIf you have any questions about this technology or how it may benefit your upcoming procedure, please don't hesitate to contact our office.`,
      ctaText: "Schedule Your Consultation Today",
      description: "Announce the availability of Videra Surgical technology to patients with emphasis on precision and outcomes",
      isNew: true
    },
    {
      id: 2,
      title: "Understanding Tissue Marking in Modern Surgery",
      category: "educational",
      subject: "What You Need to Know About Surgical Tissue Marking",
      previewText: "Learn how advanced tissue marking technology improves surgical outcomes",
      bodyContent: `Dear Valued Patient,\n\nAs part of our commitment to patient education, we want to share information about an important aspect of modern surgical procedures: tissue marking.\n\nTissue marking is a critical step in surgical planning that helps surgeons identify precise locations for incisions and interventions. With advanced tools like Videra Surgical's adaptable tissue marker, this process has become more accurate and efficient than ever before.\n\nWhat is Tissue Marking?\nTissue marking involves placing visible indicators on specific areas of tissue to guide surgical procedures. This ensures accuracy and helps minimize unnecessary tissue disruption.\n\nWhy It Matters:\n• Ensures surgical precision\n• Reduces procedure time\n• Minimizes tissue trauma\n• Improves recovery outcomes\n• Enhances communication between surgical team members\n\nOur practice uses state-of-the-art tissue marking technology to provide you with the safest, most effective surgical care possible.\n\nWe encourage you to ask questions about any aspect of your surgical procedure, including the tools and techniques we use.`,
      ctaText: "Learn More About Our Surgical Approach",
      description: "Educational content explaining tissue marking technology and its benefits for patient understanding"
    },
    {
      id: 3,
      title: "Your Safety is Our Priority",
      category: "trust",
      subject: "How We Ensure Surgical Excellence at [Practice Name]",
      previewText: "Discover the advanced technology and protocols that keep you safe",
      bodyContent: `Dear [Patient Name],\n\nYour trust in our surgical practice means everything to us. We want you to know about the comprehensive measures we take to ensure your safety and the success of your procedure.\n\nAdvanced Technology:\nWe invest in cutting-edge surgical tools like Videra Surgical's adaptable tissue marker, which provides unparalleled precision in surgical planning and execution.\n\nOur Commitment to Excellence:\n• Board-certified surgical team with extensive experience\n• State-of-the-art surgical facility with latest technology\n• Rigorous safety protocols and quality standards\n• Continuous training on newest techniques and tools\n• Personalized care tailored to your unique needs\n\nEvery member of our team is dedicated to providing you with the highest level of care, from your initial consultation through your complete recovery.\n\nWe understand that undergoing surgery can be stressful, and we're here to address any concerns you may have. Your comfort, safety, and successful outcome are our top priorities.\n\nThank you for choosing our practice for your surgical care.`,
      ctaText: "Contact Us With Any Questions",
      description: "Build patient confidence by highlighting safety measures, technology, and commitment to excellence"
    },
    {
      id: 4,
      title: "Spring Into Better Health",
      category: "seasonal",
      subject: "Spring is the Perfect Time for Your Procedure",
      previewText: "Take advantage of the season to prioritize your health",
      bodyContent: `Dear [Practice Name] Patients,\n\nSpring is a time of renewal and fresh starts – making it an ideal time to address any surgical needs you may have been considering.\n\nWhy Spring?\n• Comfortable recovery weather\n• Time to heal before summer activities\n• Insurance benefits often reset in new year\n• Reduced seasonal illness exposure\n• Flexible scheduling availability\n\nOur practice is equipped with the latest surgical technology, including Videra Surgical's innovative tissue marking system, to ensure your procedure is performed with maximum precision and minimal disruption to your daily life.\n\nSpring Scheduling Benefits:\n• Extended consultation availability\n• Flexible procedure dates\n• Comprehensive pre-operative planning\n• Dedicated post-operative support\n\nDon't let another season pass without addressing your health concerns. Our experienced surgical team is ready to help you achieve your health goals with the most advanced tools and techniques available.\n\nContact us today to schedule your consultation and take the first step toward better health this spring.`,
      ctaText: "Schedule Your Spring Consultation",
      description: "Seasonal campaign encouraging patients to schedule procedures during optimal spring timing",
      isNew: false
    },
    {
      id: 5,
      title: "Advanced Surgical Technology: What It Means for You",
      category: "technology",
      subject: "How Modern Tools Improve Your Surgical Experience",
      previewText: "Discover the technology behind better surgical outcomes",
      bodyContent: `Dear Valued Patient,\n\nMedical technology continues to advance at a remarkable pace, and our practice is committed to bringing you the benefits of these innovations.\n\nVidera Surgical Technology:\nOur practice now utilizes Videra Surgical's adaptable tissue marker, a breakthrough tool that exemplifies how modern technology enhances surgical precision.\n\nWhat This Means for You:\n• More accurate surgical planning\n• Reduced procedure time\n• Minimized tissue disruption\n• Faster recovery periods\n• Improved long-term outcomes\n• Enhanced surgical team coordination\n\nThe curved, adaptable design of Videra Surgical allows our surgical team to mark tissue with unprecedented accuracy, even in challenging anatomical locations. This precision translates directly to better outcomes for you.\n\nOur Investment in Your Care:\nWe continuously evaluate and adopt new technologies that demonstrate clear benefits for patient safety and outcomes. Every tool we use is selected based on rigorous evidence and our commitment to providing you with the best possible care.\n\nYour surgical experience matters to us, and we believe that investing in advanced technology is one of the most important ways we can serve you.`,
      ctaText: "Explore Our Advanced Surgical Capabilities",
      description: "Detailed explanation of how modern surgical technology benefits patients with focus on Videra Surgical"
    },
    {
      id: 6,
      title: "Preparing for Your Surgical Procedure",
      category: "educational",
      subject: "Your Complete Pre-Operative Guide",
      previewText: "Everything you need to know before your procedure",
      bodyContent: `Dear [Patient Name],\n\nAs your procedure date approaches, we want to ensure you feel fully prepared and confident. This guide covers important information about what to expect.\n\nBefore Your Procedure:\n• Follow all pre-operative instructions provided\n• Arrange for transportation to and from the facility\n• Prepare your recovery space at home\n• Fill any prescribed medications in advance\n• Plan for time off work or daily activities\n\nWhat to Expect:\nOur surgical team uses advanced technology, including Videra Surgical's precision tissue marking system, to ensure every aspect of your procedure is planned and executed with meticulous care.\n\nDay of Procedure:\n• Arrive at scheduled time\n• Complete final pre-operative checks\n• Meet with surgical team\n• Receive detailed post-operative instructions\n\nYour Surgical Team:\nEvery member of our team is dedicated to your safety, comfort, and successful outcome. We use the latest techniques and technology to provide you with exceptional care.\n\nQuestions or Concerns?\nPlease don't hesitate to contact our office if you have any questions before your procedure. We're here to support you every step of the way.`,
      ctaText: "Contact Our Pre-Operative Team",
      description: "Comprehensive pre-operative education email helping patients prepare for their surgical procedure"
    },
    {
      id: 7,
      title: "Why Choose Our Practice for Your Surgical Care",
      category: "trust",
      subject: "Experience the [Practice Name] Difference",
      previewText: "Discover what sets our surgical practice apart",
      bodyContent: `Dear Prospective Patient,\n\nChoosing a surgical practice is one of the most important healthcare decisions you'll make. We want you to understand what makes our practice uniquely qualified to provide your care.\n\nOur Commitment to Excellence:\n• Board-certified surgeons with extensive experience\n• Investment in latest surgical technology like Videra Surgical\n• Personalized care plans tailored to your needs\n• Comprehensive pre and post-operative support\n• Outstanding patient satisfaction ratings\n\nAdvanced Technology:\nWe believe in providing our patients with access to the most advanced surgical tools available. Our use of Videra Surgical's adaptable tissue marker exemplifies our commitment to precision and innovation.\n\nPatient-Centered Care:\n• Thorough consultation and education\n• Clear communication throughout your journey\n• Accessible team for questions and concerns\n• Comfortable, modern surgical facility\n• Dedicated follow-up care\n\nOur Philosophy:\nWe treat every patient as we would want our own family members treated – with respect, compassion, and the highest standard of medical care.\n\nThousands of patients have trusted us with their surgical care, and we would be honored to serve you.`,
      ctaText: "Schedule Your Consultation",
      description: "Trust-building email highlighting practice differentiators and commitment to patient-centered care"
    },
    {
      id: 8,
      title: "National Health Awareness Month: Take Action",
      category: "seasonal",
      subject: "Join Us in Promoting Better Health This Month",
      previewText: "Special focus on surgical health and wellness",
      bodyContent: `Dear [Practice Name] Community,\n\nThis month marks National Health Awareness Month, and we're joining healthcare providers nationwide in promoting proactive health management.\n\nWhy Awareness Matters:\nMany surgical conditions benefit from early intervention. Understanding your options and taking timely action can significantly impact your outcomes and quality of life.\n\nOur Role in Your Health:\nWe're committed to providing you with:\n• Access to advanced surgical technology\n• Comprehensive patient education\n• Expert surgical care with precision tools like Videra Surgical\n• Supportive, compassionate healthcare team\n• Evidence-based treatment approaches\n\nTake Action This Month:\n• Schedule overdue consultations\n• Address health concerns you've been postponing\n• Learn about surgical options that may benefit you\n• Discuss preventive care strategies\n• Share health information with family members\n\nSpecial Awareness Month Benefits:\n• Extended consultation hours\n• Comprehensive health assessments\n• Educational resources and materials\n• Direct access to our surgical team\n\nYour health is your most valuable asset. This awareness month, take the step to prioritize your wellbeing and explore how modern surgical solutions can improve your quality of life.`,
      ctaText: "Schedule Your Awareness Month Consultation",
      description: "Seasonal awareness campaign encouraging patients to take proactive steps toward better health"
    }
  ]);

  const filteredTemplates = useMemo(() => {
    let filtered = emailTemplates;

    if (activeCategory !== 'all') {
      filtered = filtered?.filter(template => template?.category === activeCategory);
    }

    if (searchQuery?.trim()) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(template =>
        template?.title?.toLowerCase()?.includes(query) ||
        template?.subject?.toLowerCase()?.includes(query) ||
        template?.description?.toLowerCase()?.includes(query) ||
        template?.bodyContent?.toLowerCase()?.includes(query)
      );
    }

    return filtered;
  }, [activeCategory, searchQuery, emailTemplates]);

  const handleResetFilters = () => {
    setActiveCategory('all');
    setSearchQuery('');
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setIsEditModalOpen(true);
  };

  const handleSaveTemplate = (updatedTemplate) => {
    setEmailTemplates(prev => 
      prev?.map(t => t?.id === updatedTemplate?.id ? updatedTemplate : t)
    );
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingTemplate(null);
  };

  return (
    <>
      <Helmet>
        <title>Email Campaigns - Videra Surgical Providers Portal</title>
        <meta name="description" content="Access ready-to-use email templates for patient communication with complete copy and formatting for medical practice marketing" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Sidebar />

        <div className="lg:ml-60">
          <header className="sticky top-0 z-50 bg-white border-b border-border shadow-sm">
            <div className="flex items-center justify-between h-20 px-4 md:px-6 lg:px-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--corvid-background-color, rgba(var(--bg, var(--color_17, 37, 99, 235)), var(--alpha-bg, 1)))' }}>
                  <Icon name="Mail" size={24} color="#1E293B" />
                </div>
                <div>
                  <h1 className="text-xl md:text-2xl font-light uppercase text-foreground">Email Campaigns</h1>
                  <p className="text-xs md:text-sm text-muted-foreground font-caption">Ready-to-use email templates for patient communication</p>
                </div>
              </div>
              <UserAccountMenu />
            </div>
          </header>

          <main className="p-4 md:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
              <div className="mb-6">
                <div className="relative">
                  <Icon 
                    name="Search" 
                    size={20} 
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                  />
                  <Input
                    type="search"
                    placeholder="Search email templates by title, subject, or content..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="pl-12"
                  />
                </div>
              </div>

              <CategoryFilter 
                activeCategory={activeCategory}
                onCategoryChange={setActiveCategory}
              />

              <EmailStatsBar 
                totalTemplates={emailTemplates?.length}
                filteredCount={filteredTemplates?.length}
                activeCategory={activeCategory}
              />

              {filteredTemplates?.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                  {filteredTemplates?.map((template) => (
                    <EmailTemplateCard 
                      key={template?.id}
                      template={template}
                      onEdit={handleEditTemplate}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-card border border-border rounded-lg">
                  <EmptyState onResetFilters={handleResetFilters} />
                </div>
              )}
            </div>
          </main>

          <EmailEditModal
            template={editingTemplate}
            isOpen={isEditModalOpen}
            onClose={handleCloseEditModal}
            onSave={handleSaveTemplate}
          />

          <footer className="bg-card border-t border-border mt-12">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <p className="text-xs text-muted-foreground font-caption">
                  &copy; {new Date()?.getFullYear()} Videra Surgical. All rights reserved.
                </p>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </>
  );
};

export default EmailCampaigns;